import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { MainHeadline, SubDescription } from 'components/Typography'
import { NavButton, OrangeButton } from 'components/Button'
import { ShoppingBagType, StoreType } from 'types/store'
import { ShoppingBagEditor } from 'components/ShoppingBag'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { saveShoppingBags } from 'reducks/store/operations'
import { db, FirestoreDocumentSnapshot } from 'firebase/index'
import { ShoppingBagsError } from 'components/Error'

type Props = {
  isAdmin?: boolean
  storeId?: string
}

const ShoppingBagTemplate: FC<Props> = (props) => {
  const { isAdmin, storeId } = props
  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const [shoppingBags, setShoppingBags] = useState<ShoppingBagType[]>([])
  const isFilledRequired = shoppingBags.every((shoppingBag) => shoppingBag.name !== '')
  const hasShoppingBagOutsidePriceRange = shoppingBags.some(
    (shoppingBag) => shoppingBag.price < 0 || shoppingBag.price > 1000
  )

  useEffect(() => {
    if (storeRef) {
      storeRef.get().then((snapshot: FirestoreDocumentSnapshot) => {
        const data = snapshot.data() as StoreType
        if ('shopping_bags' in data) {
          setShoppingBags([...data.shopping_bags])
        }
      })
    } else {
      if (!storeId || !isAdmin) return
      db.collection('stores')
        .doc(storeId)
        .get()
        .then((snapshot: FirestoreDocumentSnapshot) => {
          const data = snapshot.data() as StoreType
          if ('shopping_bags' in data) {
            setShoppingBags([...data.shopping_bags])
          }
        })
    }
  }, [storeRef, storeId, isAdmin])

  const addShoppingBagRow = useCallback(() => {
    setShoppingBags((prevState) => {
      return [...prevState, { name: '', price: 0 }]
    })
  }, [setShoppingBags])

  const deleteShoppingBagRow = useCallback(
    (index: number) => {
      setShoppingBags((prevState) => {
        const nextState = prevState.filter((_, i) => i !== index)
        return [...nextState]
      })
    },
    [setShoppingBags]
  )

  const handleShoppingBags = useCallback(
    (index: number, name: string, price: number) => {
      setShoppingBags((prevState) => {
        prevState[index] = { name: name, price: price }
        return [...prevState]
      })
    },
    [setShoppingBags]
  )

  const saveShoppingBagsCallback = useCallback(() => {
    if (storeRef) {
      dispatch(saveShoppingBags(shoppingBags, storeRef, isAdmin))
    } else {
      if (!storeId || !isAdmin) return
      const ref = db.collection('stores').doc(storeId)
      dispatch(saveShoppingBags(shoppingBags, ref, isAdmin))
    }
  }, [shoppingBags, storeRef, isAdmin, storeId])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'レジ袋オプション'} />
      <Spacer size={8} />
      <SubDescription description={'レジ袋のサイズを入力し、レジ袋の価格を設定してください。'} />
      <Spacer size={12} />
      <ShoppingBagEditor
        addShoppingBagRow={addShoppingBagRow}
        deleteShoppingBagRow={deleteShoppingBagRow}
        handleShoppingBags={handleShoppingBags}
        shoppingBags={shoppingBags}
      />
      <Spacer size={40} />
      <OrangeButton
        disabled={!isFilledRequired || hasShoppingBagOutsidePriceRange}
        label={'保存する'}
        onClick={saveShoppingBagsCallback}
        size={'medium'}
        type={'main'}
      />
      {!isFilledRequired && (
        <>
          <Spacer size={24} />
          <ShoppingBagsError shoppingBags={shoppingBags} />
        </>
      )}
      <Spacer size={48} />
      <NavButton
        label={'商品の受渡しに関する設定に戻る'}
        iconPath={'/icon/grey/arrow_prev.svg'}
        url={'/account/settings'}
      />
      <Spacer size={12} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default ShoppingBagTemplate
